import {inject, injectable} from "inversify";
import {DisplayObject, SCALE_MODES, Sprite, Texture} from "pixi.js-legacy";
import {Types2D} from "@dp-instant/interfaces";
import {RootView} from "@dp-instant/2d";
import svgPathBbox from "svg-path-bbox";

@injectable()
export class SpritesPool {
    @inject(Types2D.rootView)
    private rootView: RootView;
    private _textures: Map<string, Texture> = new Map<string, Texture>();
    private _shapeTextures: Map<string, Texture> = new Map<string, Texture>();
    private _pendingShapeTextures: Map<string, Promise<Texture>> = new Map<
        string,
        Promise<Texture>
    >();

    fromDisplayObject(name: string, render: () => DisplayObject): Sprite {
        if (!this._textures.has(name)) {
            this._textures.set(
                name,
                this.rootView.app.renderer.generateTexture(
                    render(),
                    SCALE_MODES.LINEAR,
                    window.devicePixelRatio
                )
            );
        }
        return new Sprite(this._textures.get(name));
    }
    async createShape(id: string, path: string, keepCache = true): Promise<Sprite> {
        const name = `${id}`;

        if (!this._shapeTextures.has(name)) {
            const texture = await this.loadShapeTexture(path, id);
            texture.textureCacheIds[0] = name;
            if (keepCache) {
                this._shapeTextures.set(name, texture);
            } else {
                const sprite = new Sprite(texture);
                Texture.removeFromCache(texture);
                return sprite;
            }
        }
        return new Sprite(this._shapeTextures.get(name));
    }
    protected async loadShapeTexture(path: string, id: string): Promise<Texture> {
        let promise: Promise<Texture> =
            this._pendingShapeTextures.get(id) || this._loadShapeTexture(path);
        this._pendingShapeTextures.set(id, promise);
        return promise;
    }
    protected async _loadShapeTexture(path: string): Promise<Texture> {
        let [x, y, width, height] = svgPathBbox(path);
        width -= x;
        height -= y;
        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="${x} ${y} ${width} ${height}" fill="white"><path d="${path}"/></svg>`;
        return Texture.fromURL(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`);
    }
    purge() {
        this._shapeTextures.forEach(Texture.removeFromCache);
        this._textures.forEach(Texture.removeFromCache);
        this._pendingShapeTextures.clear();
        this._textures.clear();
        this._shapeTextures.clear();
    }
}
